import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError, BrandingImageKind, StorefrontBranding, StorefrontContent, StorefrontCopy } from '../api';
import { brandingEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Button, ErrorNote, Panel } from '../ui';
import { INPUT } from './event/shared';
import { DEFAULT_TERMS, TERMS_PRESETS, parseTerms, presetOf, termsFor } from '@bre-ticketing/domain';
import type { TermsPreset, TermsSetting } from '@bre-ticketing/domain';
import { rememberTerms } from '../terms';

/** The terminology fields (D-378), in the order the form shows them. */
const TERMS_FIELDS: { key: keyof TermsSetting; label: string; hint: string }[] = [
  { key: 'event_one', label: 'An event', hint: 'What customers call an event without an opponent. An event with an opponent is always a “game”.' },
  { key: 'event_many', label: 'Events', hint: 'The plural.' },
  { key: 'date_one', label: 'One date (staff)', hint: 'What staff screens call one dated occurrence: the tab, “Add a …”, the scanner’s “Wrong …”.' },
  { key: 'date_many', label: 'Dates (staff)', hint: 'The plural: the tab name and the scanner’s list.' },
  { key: 'gates', label: 'Opening time', hint: 'Printed beside the opening time on tickets, emails and event pages.' },
  { key: 'game_starts', label: 'A game starts', hint: 'Beside a game’s start time on its page.' },
];

const PRESET_LABELS: Record<TermsPreset, string> = { generic: 'Generic venue', baseball: 'Ballpark (baseball)' };

/**
 * Storefront branding (D-126, D-367).
 *
 * The organization's logo and name, the colors the storefront is drawn in, how
 * to reach the box office, and the team's own words and pages. It used to be
 * environment variables, which meant the person who owns the brand could not
 * change it and a color cost a deploy.
 *
 * The preview is the point of the page: a color is not a hex string to
 * anybody, and picking one without seeing the button it makes is guesswork.
 */

const LOGOS: { kind: BrandingImageKind; label: string; hint: string }[] = [
  { kind: 'header', label: 'Header logo', hint: 'Sits beside the team name in the header bar.' },
  { kind: 'footer', label: 'Footer logo', hint: 'Falls back to the header logo when empty.' },
  { kind: 'icon', label: 'Square icon', hint: 'Favicon and compact places. Square artwork works best.' },
  { kind: 'hero', label: 'Home page photo', hint: 'Beside the headline on the home page and the schedule banner; landscape, up to 3 MB. Without one the space is a patterned band.' },
];

const COLORS = [
  { key: 'color_brand' as const, label: 'Brand', hint: 'Header, footer, dark bands, secondary buttons.' },
  { key: 'color_brand_hover' as const, label: 'Brand (dark)', hint: 'A shade darker: hover, and the deepest bands.' },
  { key: 'color_background' as const, label: 'Page', hint: 'The off-white behind everything.' },
  { key: 'color_surface_muted' as const, label: 'Muted surface', hint: 'Form fields and quiet panels.' },
  { key: 'color_accent' as const, label: 'Accent', hint: 'Primary buttons, kickers, underlines. Keep it light: a button’s label is the brand color.' },
];

/** The team's words (D-367). The placeholders are the first team's, as an example of each. */
const COPY: { key: keyof StorefrontCopy; label: string; hint: string; placeholder: string; long?: boolean }[] = [
  { key: 'hero_kicker', label: 'Home kicker', hint: 'Small gold capitals above the headline, and on the schedule banner.', placeholder: 'Small town. Big baseball.' },
  { key: 'hero_headline', label: 'Home headline', hint: 'The big word. Empty uses the organization name.', placeholder: 'Baseball' },
  { key: 'hero_script', label: 'Home script line', hint: 'Brush script under the headline.', placeholder: 'at the Hive' },
  { key: 'hero_intro', label: 'Home welcome', hint: 'A sentence or two under the headline.', placeholder: 'Affordable, family-friendly fun all summer long.', long: true },
  { key: 'tagline', label: 'Footer tagline', hint: 'Gold capitals under the name in the footer.', placeholder: 'Baseball brings us together.' },
  { key: 'slogan', label: 'Schedule banner', hint: 'Brush script in the schedule’s sidebar.', placeholder: 'Good people, great baseball' },
  { key: 'cheer', label: 'Footer cheer', hint: 'Brush script sign-off in the footer.', placeholder: 'Go Bees!' },
];

/** The content pages (D-367), as Markdown. */
const CONTENT: { key: keyof StorefrontContent; label: string; hint: string }[] = [
  { key: 'parking_md', label: 'Parking & directions', hint: 'Ballpark Info → Parking. Where to park, what it costs, how to get here.' },
  { key: 'seating_md', label: 'Seating', hint: 'Ballpark Info → Seating. The seating areas and what each is like.' },
  { key: 'policies_md', label: 'Stadium policies', hint: 'Ballpark Info → Policies. Bags, re-entry, smoking, what to bring.' },
  { key: 'accessibility_md', label: 'Accessibility', hint: 'Ballpark Info → Accessibility. Wheelchair seating, parking, assistance.' },
  { key: 'about_md', label: 'About', hint: 'Ballpark Info → About. The team and the ballpark.' },
  { key: 'group_outings_md', label: 'Group outings', hint: 'The Group Outings page: packages, group sizes, how to book.' },
  { key: 'privacy_md', label: 'Privacy policy', hint: 'The Privacy Policy page linked from the footer.' },
];

export function BrandingPage() {
  const can = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<StorefrontBranding | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const pending = useRef<BrandingImageKind>('header');

  const branding = useQuery<{ branding: StorefrontBranding }, ApiError>({ queryKey: ['branding'], queryFn: brandingEndpoints.get });
  useEffect(() => {
    if (branding.data) setForm(branding.data.branding);
  }, [branding.data]);

  const onError = (cause: ApiError) => setError(cause.message);
  const save = useMutation({
    mutationFn: () => brandingEndpoints.save(form!),
    // The staff screens say the new words at once (D-378).
    onSuccess: (result) => {
      rememberTerms(result.branding.terms);
      void branding.refetch();
    },
    onError,
  });
  const upload = useMutation({
    mutationFn: ({ kind, blob }: { kind: BrandingImageKind; blob: File }) => brandingEndpoints.uploadLogo(kind, blob),
    onSuccess: () => void branding.refetch(),
    onError,
  });
  const removeLogo = useMutation({ mutationFn: (kind: BrandingImageKind) => brandingEndpoints.removeLogo(kind), onSuccess: () => void branding.refetch(), onError });

  if (!can('settings.manage')) return <NotPermitted permission="settings.manage" />;
  if (branding.error) return <ErrorNote>{branding.error.message}</ErrorNote>;
  if (!form) return <p className="text-sm text-ink-400">Loading…</p>;

  const set = <K extends keyof StorefrontBranding>(key: K, value: StorefrontBranding[K]) => setForm({ ...form, [key]: value });
  const setCopy = (key: keyof StorefrontCopy, value: string) => setForm({ ...form, copy: { ...form.copy, [key]: value || null } });
  const setContent = (key: keyof StorefrontContent, value: string) => setForm({ ...form, content: { ...form.content, [key]: value || null } });
  const terms = parseTerms(form.terms ?? DEFAULT_TERMS);
  const setTerm = (key: keyof TermsSetting, value: string) => setForm({ ...form, terms: { ...terms, [key]: value } });
  const preset = presetOf(terms);
  const saveButton = (
    <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
      {save.isPending ? 'Saving…' : 'Save'}
    </Button>
  );

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          if (chosen) upload.mutate({ kind: pending.current, blob: chosen });
          event.target.value = '';
        }}
      />

      <Panel
        title="Storefront branding"
        description="Fans see “(Organization) Ticketing”. The logo sits beside the name, and these colors are what the whole public site is drawn in."
        actions={saveButton}
      >
        <div className="grid gap-6 px-5 py-4 lg:grid-cols-2">
          <div className="space-y-5">
            <div className="space-y-3">
              {LOGOS.map(({ kind, label, hint }) => {
                const url = kind === 'header' ? form.logo_header_url : kind === 'footer' ? form.logo_footer_url : kind === 'icon' ? form.logo_icon_url : form.hero_image_url;
                return (
                  <div key={kind}>
                    <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
                    <p className="text-xs text-ink-400">{hint}</p>
                    {/* On the brand color, because that is where it will sit. */}
                    {url ? (
                      <div className="mt-1 inline-flex rounded border border-line p-2" style={{ background: form.color_brand }}>
                        <img src={`${url}?v=${branding.dataUpdatedAt}`} alt="" className="max-h-16 w-auto object-contain" />
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-ink-400">{kind === 'hero' ? 'None.' : 'None — the name shows on its own.'}</p>
                    )}
                    <div className="mt-1 flex gap-2">
                      <Button
                        disabled={upload.isPending}
                        onClick={() => {
                          pending.current = kind;
                          file.current?.click();
                        }}
                      >
                        Upload
                      </Button>
                      {url ? (
                        <Button variant="danger" onClick={() => removeLogo.mutate(kind)}>
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-ink-400">
                PNG, JPEG or WebP; logos 1 MB or less, the photo 3 MB. SVG is not accepted: it can carry script and this image is on every page. The logo keeps its
                aspect ratio — only its height is set, so nothing is ever squashed.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <label className="text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Logo description</span>
                <input value={form.logo_alt} onChange={(e) => set('logo_alt', e.target.value)} placeholder="Burlington Bees" className={`mt-1 w-72 ${INPUT}`} />
              </label>
              <label className="text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Header height</span>
                <input type="number" min={16} max={160} value={form.logo_header_height} onChange={(e) => set('logo_header_height', Number(e.target.value))} className={`tabular mt-1 w-24 ${INPUT}`} />
              </label>
              <label className="text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Footer height</span>
                <input type="number" min={16} max={160} value={form.logo_footer_height} onChange={(e) => set('logo_footer_height', Number(e.target.value))} className={`tabular mt-1 w-24 ${INPUT}`} />
              </label>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {COLORS.map(({ key, label, hint }) => (
                <label key={key} className="text-sm">
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
                  <span className="mt-1 flex items-center gap-2">
                    <input type="color" value={form[key]} onChange={(e) => set(key, e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-line" aria-label={label} />
                    <input value={form[key]} onChange={(e) => set(key, e.target.value)} className={`w-28 font-mono ${INPUT}`} />
                  </span>
                  <span className="text-xs text-ink-400">{hint}</span>
                </label>
              ))}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input type="checkbox" checked={form.texture_enabled} onChange={(e) => set('texture_enabled', e.target.checked)} />
                subtle pattern on the dark bands and pinstripes on the page
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input type="checkbox" checked={form.show_platform_attribution} onChange={(e) => set('show_platform_attribution', e.target.checked)} />
                show the platform’s “powered by” line in the footer
              </label>
              <p className="text-xs text-ink-400">Off by default.</p>
            </div>
          </div>

          <div className="space-y-4">
            <BrandPreview branding={form} />
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Organization</span>
                <input value={form.copyright_holder ?? ''} onChange={(e) => set('copyright_holder', e.target.value || null)} placeholder="Burlington Bees" className={`mt-1 w-full ${INPUT}`} />
                <span className="text-xs text-ink-400">
                  The team or organization. The storefront is “{form.copyright_holder?.trim() || 'Organization'} Ticketing”; the header wordmark and the copyright line use it, and receipts thank it by
                  name. Leave it empty to show the platform’s name alone.
                </span>
              </label>
              <label className="block text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Address</span>
                <textarea
                  value={form.address.join('\n')}
                  onChange={(e) => set('address', e.target.value.split('\n'))}
                  rows={3}
                  className={`mt-1 w-full ${INPUT}`}
                  placeholder={'Community Field\n2712 Mt Pleasant St\nBurlington, IA 52601'}
                />
                <span className="text-xs text-ink-400">One line each, as it should be printed.</span>
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="text-sm">
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Phone</span>
                  <input value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value || null)} className={`mt-1 w-40 ${INPUT}`} />
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Email</span>
                  <input value={form.email ?? ''} onChange={(e) => set('email', e.target.value || null)} className={`mt-1 w-56 ${INPUT}`} />
                </label>
              </div>
              <div className="flex flex-wrap gap-3">
                {(
                  [
                    ['facebook_url', 'Facebook'],
                    ['instagram_url', 'Instagram'],
                    ['x_url', 'X'],
                    ['youtube_url', 'YouTube'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="text-sm">
                    <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
                    <input value={form[key] ?? ''} onChange={(e) => set(key, e.target.value || null)} placeholder="https://…" className={`mt-1 w-64 ${INPUT}`} />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Panel>

      <Panel
        title="Storefront words"
        description="The lines the storefront says about the team. Anything left empty is not shown, except the headline, which falls back to the organization name."
        actions={saveButton}
      >
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
          {COPY.map(({ key, label, hint, placeholder, long }) => (
            <label key={key} className={`block text-sm ${long ? 'sm:col-span-2' : ''}`}>
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
              {long ? (
                <textarea value={form.copy[key] ?? ''} onChange={(e) => setCopy(key, e.target.value)} rows={2} maxLength={400} placeholder={placeholder} className={`mt-1 w-full ${INPUT}`} />
              ) : (
                <input value={form.copy[key] ?? ''} onChange={(e) => setCopy(key, e.target.value)} maxLength={120} placeholder={placeholder} className={`mt-1 w-full ${INPUT}`} />
              )}
              <span className="text-xs text-ink-400">{hint}</span>
            </label>
          ))}
        </div>
      </Panel>

      <Panel
        title="Terminology"
        description="What this deployment calls what it sells (D-378). Customers never read “performance”: an event with an opponent is a game, anything else is the event word below. Staff screens use the date word for one dated occurrence. A preset fills every field; each can still be changed."
        actions={saveButton}
      >
        <div className="px-5 py-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Preset</span>
            {(Object.keys(TERMS_PRESETS) as TermsPreset[]).map((name) => (
              <Button key={name} variant={preset === name ? 'primary' : 'secondary'} onClick={() => setForm({ ...form, terms: { ...TERMS_PRESETS[name] } })}>
                {PRESET_LABELS[name]}
              </Button>
            ))}
            {preset === null ? <span className="text-xs text-ink-400">Custom</span> : null}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TERMS_FIELDS.map(({ key, label, hint }) => (
              <label key={key} className="block text-sm">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
                <input value={terms[key]} onChange={(e) => setTerm(key, e.target.value)} maxLength={40} className={`mt-1 w-full ${INPUT}`} />
                <span className="text-xs text-ink-400">{hint}</span>
              </label>
            ))}
          </div>
          <p className="mt-4 text-xs text-ink-500">
            Reads as: “{termsFor(terms, { opponent: true }).A} tonight · {terms.gates} 6:00 PM · {terms.game_starts} 7:05 PM”, “{termsFor(terms).A} this weekend”, and on staff
            screens “Add {termsFor(terms, { kind: 'date' }).a}” under the {termsFor(terms, { kind: 'date' }).Many} tab.
          </p>
        </div>
      </Panel>

      <Panel
        title="Storefront pages"
        description="Ballpark Info, Group Outings and the Privacy Policy, in Markdown: # headings, a blank line between paragraphs, - lists, **bold**, [links](https://…) and | tables |. An empty section is left off its page."
        actions={saveButton}
      >
        <div className="grid gap-4 px-5 py-4 lg:grid-cols-2">
          {CONTENT.map(({ key, label, hint }) => (
            <label key={key} className="block text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
              <textarea value={form.content[key] ?? ''} onChange={(e) => setContent(key, e.target.value)} rows={8} maxLength={20000} className={`mt-1 w-full font-mono text-xs ${INPUT}`} />
              <span className="text-xs text-ink-400">{hint}</span>
            </label>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/** The header and a game row as the customer will see them, in the colors currently chosen. */
function BrandPreview({ branding }: { branding: StorefrontBranding }) {
  const name = branding.copyright_holder?.trim() || 'Organization';
  return (
    <div>
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Preview</p>
      <div className="mt-1 overflow-hidden rounded border border-line" style={{ background: branding.color_background }}>
        <div className="flex items-center gap-3 px-4 py-3" style={{ background: branding.color_brand }}>
          {branding.logo_header_url ? <img src={branding.logo_header_url} alt="" style={{ height: Math.min(40, branding.logo_header_height) }} className="w-auto object-contain" /> : null}
          <span className="leading-none">
            <span className="block text-base font-bold tracking-tight text-white uppercase">{name}</span>
            <span className="block text-[10px] font-semibold tracking-[0.3em] uppercase" style={{ color: branding.color_accent }}>
              Tickets
            </span>
          </span>
          <span className="ml-auto rounded px-3 py-1.5 text-[11px] font-bold uppercase" style={{ background: branding.color_accent, color: branding.color_brand }}>
            Buy tickets
          </span>
        </div>
        <div className="space-y-2 p-4">
          <p className="text-[10px] font-semibold tracking-[0.25em] uppercase" style={{ color: branding.color_brand }}>
            {branding.copy.hero_kicker || 'Upcoming games'}
          </p>
          <p className="text-xl font-bold uppercase" style={{ color: branding.color_brand }}>
            {branding.copy.hero_headline || name}
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="rounded px-3 py-1.5 text-xs font-bold uppercase" style={{ background: branding.color_accent, color: branding.color_brand }}>
              Buy now
            </span>
            <span className="rounded px-3 py-1.5 text-xs font-bold text-white uppercase" style={{ background: branding.color_brand }}>
              View schedule
            </span>
            <span className="rounded px-3 py-1.5 text-xs" style={{ background: branding.color_surface_muted, color: '#0f1b2d' }}>
              Add to calendar
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
